import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NavigationItem } from '@models/navigation-item/1.0/navigation-item';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { ComponentRegistryService } from '@services/component-registry/component-registry.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { NgFor, NgIf, NgClass, AsyncPipe } from '@angular/common';
import { ButtonComponent } from '../button/1.0/button.component';

@Component({
  selector: 'txp-vertical-nav',
  templateUrl: './vertical-nav.component.html',
  styleUrls: ['./vertical-nav.component.scss'],
  standalone: true,
  imports: [NgFor, NgIf, ButtonComponent, NgClass, AsyncPipe],
})
export class VerticalNavComponent implements OnChanges, OnDestroy {
  @Input() navigationItems: NavigationItem[];
  @Output() childClick = new EventEmitter<NavigationItem>();

  buttonStyle = ButtonStyle;
  buttonSize = ButtonSize;
  private destroy$ = new Subject<void>();

  constructor(
    private permissionsService: PermissionsService,
    private router: Router,
    private route: ActivatedRoute,
    private componentRegistryService: ComponentRegistryService,
    private cdr: ChangeDetectorRef,
  ) {
    // Listen for navigation events to update selection when component is reused from route storage
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        // Small delay to allow parent component to update navigation items first
        // This is necessary when component is retrieved from RouteReloadStrategy storage
        setTimeout(() => {
          this.ensureCorrectSelection();
        }, 0);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Lifecycle hook that runs when navigationItems input changes.
   * Ensures the correct item is selected based on the current URL.
   * This fixes the issue where selection is lost when navigating back to a page.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['navigationItems'] && this.navigationItems) {
      this.ensureCorrectSelection();
    }
  }

  /**
   * Ensures that the correct navigation item is selected based on the current URL.
   * If no item is selected, or if the selected item doesn't match the current URL,
   * this method finds and selects the correct item.
   */
  private ensureCorrectSelection(): void {
    // Skip if navigation items haven't been initialized yet
    if (!this.navigationItems || this.navigationItems.length === 0) {
      return;
    }

    const currentUrl = this.router.url.split('?')[0].split('#')[0]; // Remove query params and fragments

    // Build a flat list of all navigation items with their links
    const allItems: { item: NavigationItem; parent: NavigationItem }[] = [];
    for (const parent of this.navigationItems) {
      for (const child of parent.children || []) {
        if (child.link) {
          allItems.push({ item: child, parent });
        }
      }
    }

    // Extract the last segment of the current URL (e.g., "info" from "/brand/.../info")
    const urlSegments = currentUrl.split('/').filter((s) => s.length > 0);
    const currentSegment = urlSegments[urlSegments.length - 1];

    // Find the best matching item (longest matching link)
    let bestMatch: { item: NavigationItem; parent: NavigationItem } | null = null;
    let longestMatchLength = 0;

    for (const { item, parent } of allItems) {
      // Extract the last segment of the item link (e.g., "content" from "./content")
      const linkSegments = item.link.split('/').filter((s) => s.length > 0 && s !== '.');
      const itemSegment = linkSegments[linkSegments.length - 1];

      // Match based on the last segment
      if (currentSegment === itemSegment) {
        const matchLength = itemSegment.length;
        if (matchLength > longestMatchLength) {
          bestMatch = { item, parent };
          longestMatchLength = matchLength;
        }
      }
    }

    // Update selection if we found a match
    if (bestMatch) {
      // Clear all selections first
      this.navigationItems.forEach((p) => {
        p.children?.forEach((c) => (c.selected = false));
      });
      // Select the best matching item
      bestMatch.item.selected = true;

      // Manually trigger change detection to update the UI
      // This is necessary when the component is retrieved from RouteReloadStrategy storage
      this.cdr.detectChanges();
    }
  }

  /**
   * Creates a full URL from a relative link.
   * Handles both absolute links (starting with /) and relative links.
   */
  private createFullUrl(link: string): string {
    if (link.startsWith('/')) {
      return link;
    }

    // For relative links, try to resolve them
    // This is a simplified approach - may need adjustment based on actual link format
    return link;
  }

  /**
   * Normalizes a URL by removing trailing slashes, query params, and fragments.
   * Ensures comparison works consistently for parent/child navigation scenarios.
   */
  private normalizeUrl(url: string): string {
    const [path] = url.split('?');
    const [withoutFragment] = path.split('#');
    if (withoutFragment === '/') {
      return withoutFragment;
    }
    return withoutFragment.replace(/\/+$/, '');
  }

  /**
   * Handles child navigation click with automatic same-route reload support.
   * If navigating to the same route, adds _reload query parameter to force component reload.
   * Emits childClick event after navigation for parent components to update UI state.
   *
   * @param nav - child nav
   * @param parent - parent of the child nav
   */
  handleChildClick(nav: NavigationItem, parent: NavigationItem) {
    nav.parent = parent;

    if (nav.link) {
      // Check if we're navigating to the same route
      const targetUrlTree = this.router.createUrlTree([nav.link], {
        relativeTo: this.route,
      });
      const targetUrl = this.router.serializeUrl(targetUrlTree);
      const currentUrl = this.router.url.split('?')[0]; // Remove query params for comparison
      const targetUrlWithoutQuery = targetUrl.split('?')[0];

      const isSameRoute =
        this.normalizeUrl(targetUrlWithoutQuery) === this.normalizeUrl(currentUrl);

      if (isSameRoute) {
        // Same route: Handle reload with guard check and selection update
        this.checkCanDeactivateAndReload(nav, parent);
      } else {
        // Different route: Emit event for parent to handle navigation and selection
        // Parent components already have their own logic for navigation/selection
        this.childClick.emit(nav);
      }
    } else {
      // If no link, just emit the event
      this.childClick.emit(nav);
    }
  }

  /**
   * Checks CanDeactivate guard before reloading the route.
   * If guard passes, proceeds with reload. If guard fails (user cancels), no navigation occurs.
   *
   * @param nav - NavigationItem to navigate to after guard check
   * @param parent - Parent NavigationItem
   */
  private async checkCanDeactivateAndReload(
    nav: NavigationItem,
    parent: NavigationItem,
  ): Promise<void> {
    try {
      const canDeactivate = await this.componentRegistryService.checkCanDeactivate();
      if (canDeactivate) {
        // Guard passed, proceed with reload
        this.performReload(nav, parent);
      }
      // If guard returns false, do nothing (user cancelled)
    } catch (error) {
      // If guard check fails, proceed with reload anyway (fail open)
      this.performReload(nav, parent);
    }
  }

  /**
   * Performs the reload by navigating with _reload query parameter.
   *
   * @param nav - NavigationItem to navigate to
   * @param parent - Parent NavigationItem
   */
  private performReload(nav: NavigationItem, parent: NavigationItem): void {
    const separator = this.router.url.includes('?') ? '&' : '?';
    const reloadUrl = `${this.router.url}${separator}_reload=${Date.now()}`;

    this.router.navigateByUrl(reloadUrl).then((reloadNavigated: boolean) => {
      if (reloadNavigated) {
        // Immediately remove the query parameter to clean the URL
        // Use replaceUrl to avoid adding to history
        this.router
          .navigate([nav.link], { relativeTo: this.route, replaceUrl: true })
          .then(() => {
            // Update selection state before emitting event
            this.updateSelectionState(nav, parent);
            // Emit event for parent components to update UI state
            this.childClick.emit(nav);
          })
          .catch(() => {
            // Even if cleanup fails, update state and emit event
            this.updateSelectionState(nav, parent);
            this.childClick.emit(nav);
          });
      }
    });
  }

  /**
   * Updates the selection state of navigation items.
   * Clears all selections in the parent's children and marks the clicked nav as selected.
   *
   * @param nav - NavigationItem that was clicked
   * @param parent - Parent NavigationItem containing the children
   */
  private updateSelectionState(nav: NavigationItem, parent: NavigationItem): void {
    // Clear all selected children in the parent
    if (parent && parent.children) {
      parent.children.forEach((child) => {
        child.selected = false;
      });
    }
    // Mark the clicked nav as selected
    nav.selected = true;
  }

  isHidden(permission) {
    return this.permissionsService.getPermission(permission);
  }

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonStyle = ButtonStyle;
}
